import { FieldType } from "../../types";
import { passportStatus, documentType, lostType } from "../../options/passport";
import getYesNoOptions from "../../options/yes_no_options";

type InputType = FieldType["type"];
type CardType = "card";
type NewFormType =
  | "new-passport"
  | "passport-renewal"
  | "lost-stolen-passport"
  | "passport-corrections";

const statusOptions = passportStatus;

export const docType = ({ form }: { form: NewFormType }) => {
  const options = [...documentType];
  if (form !== "new-passport") options.pop();

  return {
    component: "Radio",
    name: "documentType2",
    label: "Have you ever been issued any of the following documents?",
    type: "radio" as InputType,
    validation: {
      required: "Please select the type of document.",
    },
    cols: 12,
    options: options,
    placeholder: "Select the type of document",
  };
};

export const status = ({ type, optional }: { type?: CardType; optional?: true }): FieldType => {
  const name = !type ? "passportStatus" : "passportCardStatus";
  const label = !type
    ? "Do you still have the book in your possession?"
    : "Do you still have the card in your possession?";

  return {
    component: "Select",
    name: name,
    label: label,
    type: "select" as InputType,
    placeholder: "Select an option",
    validation: {
      ...(!optional && { required: "Select an option" }),
    },
    options: statusOptions,
  };
};

export const issuanceDate = ({
  type,
  optional,
}: {
  type?: CardType;
  optional?: true;
}): FieldType => {
  const name = !type ? "passportIssuanceDate" : "cardIssuanceDate";
  const label = !type
    ? `When was your most recent book issued?${optional ? " (Optional)" : ""}`
    : `When was your most recent card issued?${optional ? " (Optional)" : ""}`;

  return {
    component: "Date",
    name: name,
    label: label,
    type: "date" as InputType,
    maxElegibleDate: "today",
    placeholder: "Please select a date.",
    validation: {
      ...(!optional && { required: "Please select a date." }),
    },
    disclaimer: `${optional && "If you don't remember this information, you can skip the fields below."}`,
  };
};

export const issuanceDateNewPassportRequired = ({ type }: { type?: CardType }): FieldType => {
  const name = !type ? "passportIssuanceDate" : "cardIssuanceDate";
  const conditionField = !type ? "passportStatus" : "passportCardStatus";

  const label = !type
    ? "When was your most recent book issued?"
    : "When was your most recent card issued?";

  return {
    component: "Date",
    name: name,
    label: label,
    type: "date" as InputType,
    maxElegibleDate: "today",
    placeholder: "Please select a date.",
    condition: {
      field: conditionField,
      values: ["yes", ""],
    },
    validation: {
      required: "Please select a date.",
    },
  };
};

export const issuanceDateNewPassportOptional = ({ type }: { type?: CardType }): FieldType => {
  const name = !type ? "passportIssuanceDate--optional" : "cardIssuanceDate--optional";
  const conditionField = !type ? "passportStatus" : "passportCardStatus";
  const label = !type
    ? "When was your most recent book issued? (Optional)"
    : "When was your most recent card issued? (Optional)";

  return {
    component: "Date",
    name: name,
    label: label,
    type: "date" as InputType,
    maxElegibleDate: "today",
    placeholder: "Please select a date.",
    condition: {
      field: conditionField,
      values: ["damaged", "lost", "stolen"],
    },
    validation: {},
  };
};

export const firstName = ({ type, optional }: { type?: CardType; optional?: true }): FieldType => {
  const name = !type ? "passportFirstName" : "cardFirstName";
  const label = optional ? "First and Middle Name (Optional)" : "First and Middle Name";
  const shortTypeName = type === "card" ? "card" : "book";

  return {
    component: "TextInput",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 23,
    placeholder: "",
    helperMessage: `Your name as printed on your most recent ${shortTypeName}`,
    validation: {
      ...(!optional && { required: "Please enter the first name." }),
      maxLength: 23,
      minLength: {
        value: 2,
        message: "The first name must have at least two characters.",
      },
    },
  };
};

export const lastName = ({ type, optional }: { type?: CardType; optional?: true }): FieldType => {
  const name = !type ? "passportLastName" : "cardLastName";
  const label = optional ? "Last Name (Optional)" : "Last Name";

  return {
    component: "TextInput",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 26,
    placeholder: "",
    validation: {
      ...(!optional && { required: "Please enter the last name." }),
      maxLength: 26,
      minLength: {
        value: 2,
        message: "The last name must have at least two characters.",
      },
    },
  };
};

export const documentNumber = ({
  type,
  optional,
}: {
  type?: CardType;
  optional?: true;
}): FieldType => {
  const name = !type ? "bookNumber" : "passportCardNumber";
  const label = !type
    ? `Book Number${optional ? " (Optional)" : ""}`
    : `Card Number${optional ? " (Optional)" : ""}`;

  return {
    component: "TextInput",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 25,
    placeholder: "",
    validation: {
      ...(!optional && { required: "Please enter the book number." }),
      maxLength: 25,
    },
  };
};

export const documentNumberNewPassportRequired = ({
  type,
  optional,
}: {
  type?: CardType;
  optional?: true;
}): FieldType => {
  const name = !type ? "bookNumber" : "passportCardNumber";
  const conditionField = !type ? "passportStatus" : "passportCardStatus";
  const label = !type ? "Book Number" : "Card Number";

  return {
    component: "TextInput",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 25,
    placeholder: "",
    condition: {
      field: conditionField,
      values: ["yes", ""],
    },
    validation: {
      required: "Please enter the book number.",
      maxLength: 25,
    },
  };
};

export const documentNumberNewPassportOptional = ({ type }: { type?: CardType }): FieldType => {
  const name = !type ? "bookNumber--optional" : "passportCardNumber--optional";
  const conditionField = !type ? "passportStatus" : "passportCardStatus";
  const label = !type ? "Book Number (Optional)" : "Card Number (Optional)";

  return {
    component: "TextInput",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 25,
    placeholder: "",
    condition: {
      field: conditionField,
      values: ["damaged", "lost", "stolen"],
    },
    validation: {
      maxLength: 25,
    },
  };
};

export const nameChanged = ({ form }: { form: NewFormType }) => {
  return {
    component: "Radio",
    name: "nameChange",
    label: "Has your name changed since your most recent passport was issued?",
    type: "radio" as InputType,
    validation: {
      required: "Please select an option.",
    },
    placeholder: "Select your reason",
    options: getYesNoOptions({}),
    value: "No",
    // tooltip: "Submit your name change documents and current passport book or card.",
    cols: 12,
  };
};

export const passportLostType = ({ type }: { type?: CardType }) => {
  const name = !type ? "passportBookLostType" : "passportCardLostType";
  const label = !type
    ? "Was your passport book lost or stolen?"
    : "Was your passport card lost or stolen?";
  return {
    component: "Radio",
    name: name,
    label: label,
    type: "radio" as InputType,
    validation: {
      required: "Please select one value.",
    },
    placeholder: "",
    options: lostType,
    value: "Lost",
  };
};

export const howLost = ({ type }: { type?: CardType }): FieldType => {
  const name = !type ? "howLostBook" : "howLostCard";
  const label = !type
    ? "Explain how the book was lost or stolen"
    : "Explain how the card was lost or stolen";

  return {
    component: "TextArea",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 84,
    placeholder: "Reason",
    validation: {
      required: "Please enter the Reason.",
      maxLength: 84,
      minLength: {
        value: 2,
        message: "The reason must have at least two characters.",
      },
    },
  };
};

export const whereLost = ({ type }: { type?: CardType }): FieldType => {
  const name = !type ? "whereLostBook" : "whereLostCard";
  const label =
    "Where did the loss or theft occur? Provide the address if known (city/state or city/country)";

  return {
    component: "TextInput",
    name: name,
    label: label,
    type: "text" as InputType,
    maxLength: 21,
    placeholder: "Location",
    validation: {
      required: "Please enter the Location.",
      maxLength: 21,
      minLength: {
        value: 2,
        message: "The location must have at least two characters.",
      },
    },
  };
};

export const dateLost = ({ type }: { type?: CardType }): FieldType => {
  const name = !type ? "bookLostDate" : "cardLostDate";
  const label = !type ? "When was the book lost or stolen?" : "When was the card lost or stolen?";

  return {
    component: "Date",
    name: name,
    label: label,
    type: "date" as InputType,
    maxElegibleDate: "today",
    placeholder: "Please select a date.",
    validation: {
      required: "Please select a date.",
    },
    disclaimerAlert:
      "<b>Note:</b> If unknown, when was the last time you remember it in your possession?",
  };
};
